import React, { useState } from "react";
import GoogleRatingBadge from "@/Components/GoogleRatingBadge";
import { router } from "@inertiajs/react";
import toast from "react-hot-toast";

import logo from "@images/logo.webp";
import ministryLogo from "@images/ministry.webp";
import vatLogo from "@images/vat.webp";
import googleIcon from "@images/icons/social/google.svg";
import metaIcon from "@images/icons/social/meta.svg";
import { ArrowLeft, ChevronLeft, ChevronDown, ShieldCheck } from "lucide-react";
import { WhatsAppIcon, SITE_SOCIAL_LINKS } from "@/Components/Icons/SocialIcons";

const servicesLinks = [
    { label: "إدارة السوشيال ميديا", href: "/services/ادارة-حسابات-التواصل-الاجتماعي" },
    { label: "الهوية البصرية والبراندينج", href: "/services/تصميم-جرافيك" },
    { label: "تصميم المواقع الإلكترونية", href: "/services/أفضل-شركة-تصميم-مواقع" },
    { label: "إدارة حملات إعلانية", href: "/services/ادارة-حملات-اعلانية" },
    { label: "موشن جرافيك", href: "/services/تصميم-موشن-جرافيك" },
    { label: "المؤثرون والمحتوى", href: "/services/التسويق-عبر-المؤثرين" },
    { label: "ربط واتساب API", href: "/ربط-واتساب-api" },
];

const fastLinks = [
    { label: "الرئيسية", href: "/" },
    { label: "من نحن", href: "/about" },
    { label: "أعمالنا", href: "/portfolio" },
    { label: "دراسات الحالة", href: "/case-studies" },
    { label: "القطاعات", href: "/industries" },
    { label: "الأسئلة الشائعة", href: "/faq" },
    { label: "تواصل معنا", href: "/contact" },
];

// Real industry landing pages (same list used in the header's "القطاعات"
// mega-menu) — a dedicated footer column so every industry page gets an
// extra crawlable internal link from the sitewide footer, not just from
// the header dropdown.
const industriesLinks = [
    { label: "تسويق المتاجر الإلكترونية", href: "/ecommerce-marketing" },
    { label: "تسويق المفروشات والمراتب", href: "/شركة-تسويق-مفروشات" },
    { label: "تسويق المطاعم والكافيهات", href: "/restaurant-marketing" },
    { label: "تسويق العيادات والمراكز الطبية", href: "/medical-marketing" },
    { label: "تسويق الشركات والمؤسسات", href: "/corporate-marketing" },
    { label: "تسويق العقارات والمقاولات", href: "/real-estate-marketing" },
    { label: "تسويق التعليم والأكاديميات", href: "/education-marketing" },
];

const FooterLinkList = ({ links }) => (
    <ul className="space-y-3 pt-5 sm:space-y-3.5 sm:pt-6">
        {links.map((link, index) => (
            <li key={index}>
                <a
                    href={link.href}
                    className="block text-right text-white font-medium hover:text-primary-300 transition-colors"
                >
                    {link.label}
                </a>
            </li>
        ))}
    </ul>
);

// Mobile-only accordion for the three link columns — same links, collapsed
// by default so the footer doesn't dump ~18 links into the scroll at once.
// Desktop keeps the always-expanded 3-column grid untouched.
const FOOTER_LINK_GROUPS = [
    { title: "روابط سريعة", links: fastLinks },
    { title: "خدماتنا", links: servicesLinks },
    { title: "القطاعات", links: industriesLinks },
];

// Desktop-only (≥1024px) footer layout: full-width container, aligned
// columns, one-row bottom bar. Mobile keeps its existing classes untouched.
const FOOTER_DESKTOP_CSS = `
.wz-footer-std-label{flex-basis:100%}
@media (max-width:639px){.wz-footer-social{gap:6px!important}}
@media (max-width:379px){.wz-footer-social{gap:3px!important}.wz-footer-social>a{width:42px!important;height:42px!important}}
@media (min-width:1024px){
.wz-footer-std-label{flex-basis:auto}
.wz-footer-wide{max-width:none!important;padding-inline:clamp(40px,5vw,96px)!important}
.wz-footer-grid{grid-template-columns:1.3fr 1fr 1.1fr 1.2fr 1.3fr!important;column-gap:clamp(32px,4vw,80px)!important;align-items:start}
.wz-footer-news{text-align:right!important;max-width:none!important;margin-inline:0!important}
.wz-footer-news .text-center{text-align:right!important}
.wz-footer-news img.mx-auto{margin-inline:0!important}
.wz-footer-news .wz-trust-badges{max-width:340px;margin-inline:0!important}
.wz-footer-bottom{display:flex;align-items:center;gap:24px;border-top:1px solid rgba(255,255,255,.1);padding-block:20px}
.wz-footer-bottom>div{border-top:0!important;padding:0!important}
.wz-footer-bottom>div:first-child{order:2;flex:1;justify-content:center!important}
.wz-footer-bottom>div:last-child{display:contents!important}
.wz-footer-bottom>div:last-child>p{order:1;margin:0;margin-inline-start:48px}
.wz-footer-bottom>div:last-child>div{order:3}
}`;

const FooterLinkAccordion = () => {
    const [openIndex, setOpenIndex] = useState(null);

    return (
        <div className="md:hidden divide-y divide-white/10 border-y border-white/10">
            {FOOTER_LINK_GROUPS.map((group, index) => {
                const isOpen = openIndex === index;
                return (
                    <div key={group.title}>
                        <button
                            type="button"
                            onClick={() => setOpenIndex(isOpen ? null : index)}
                            className="w-full flex items-center justify-between py-4 text-right"
                            aria-expanded={isOpen}
                        >
                            <h3 className="text-base font-bold text-white">{group.title}</h3>
                            <ChevronDown className={`size-4 text-white/60 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] pb-4" : "grid-rows-[0fr]"}`}>
                            <div className="overflow-hidden">
                                <ul className="space-y-3 text-right">
                                    {group.links.map((link, i) => (
                                        <li key={i}>
                                            <a href={link.href} className="block text-white font-medium hover:text-primary-300 transition-colors">
                                                {link.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const Footer = () => {
    const [newsletterEmail, setNewsletterEmail] = useState("");
    const [subscribing, setSubscribing] = useState(false);

    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        if (!newsletterEmail.trim() || subscribing) return;

        setSubscribing(true);
        router.post(
            "/newsletter/subscribe",
            { email: newsletterEmail.trim(), source: "footer" },
            {
                preserveScroll: true,
                onSuccess: () => {
                    toast.success("تم اشتراكك بنجاح في النشرة البريدية");
                    setNewsletterEmail("");
                },
                onError: (errors) => {
                    toast.error(Object.values(errors)[0] || "حدث خطأ، حاول مرة أخرى");
                },
                onFinish: () => setSubscribing(false),
            }
        );
    };

    return (
        <footer
            dir="rtl"
            className="bg-[#053585]"
            style={{ fontFamily: '"Tajawal", "IBM Plex Sans Arabic", sans-serif' }}
        >
            <style>{FOOTER_DESKTOP_CSS}</style>
            {/* Hero CTA */}
            <div className="wrapper pt-8 pb-6 sm:pt-12 sm:pb-8 text-center">
                <div className="inline-flex items-center gap-3 mb-4">
                    <span className="h-px w-8 bg-primary-300/40" />
                    <p className="text-primary-300 text-xs font-black uppercase tracking-widest">
                        شريكك في النمو الرقمي
                    </p>
                    <span className="h-px w-8 bg-primary-300/40" />
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white leading-tight">
                    ابدأ مشروعك مع{" "}
                    <span className="text-primary-300">ويز فريلانس</span>
                </h2>
                <p className="mt-4 sm:mt-5 max-w-2xl mx-auto text-white/90 text-base sm:text-lg leading-relaxed">
                    فريق من المتخصصين السعوديين في التسويق والتصميم والإعلانات — نهتم
                    بأن يكون لكل قرار أثر حقيقي على نمو نشاطك.
                </p>
                <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
                    <a
                        href="/contact"
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-white px-9 py-4 text-[#1447e6] font-black shadow-lg shadow-black/20 hover:bg-white/90 transition-colors"
                    >
                        <ArrowLeft className="size-5" />
                        ابدأ معنا الآن
                    </a>
                    <a
                        href="https://api.whatsapp.com/send/?phone=966569691027"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full border border-white/15 bg-white/10 px-8 py-4 text-white font-bold hover:bg-white/15 transition-colors"
                    >
                        <WhatsAppIcon className="size-5" />
                        واتساب
                    </a>
                </div>
            </div>

            {/* Link columns — mobile: collapsed accordion for the 3 link
                lists (all links still in the DOM) + newsletter always
                visible; desktop: unchanged always-expanded 4-column grid. */}
            <div className="wrapper wz-footer-wide pb-8">
                <FooterLinkAccordion />

                <div className="wz-footer-grid grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-x-8 gap-y-8 sm:gap-y-10">
                    {/* Reviews — first (right) column, desktop only: our own
                        Google rating card, linking to our Google Maps listing. */}
                    <div className="hidden lg:block min-w-0 text-right" style={{ order: -1 }}>
                        <h3 className="text-lg sm:text-xl font-bold text-white">آراء عملائنا</h3>
                        <GoogleRatingBadge variant="card" className="mt-5" />
                    </div>

                    {/* Quick links — desktop only, accordion covers mobile */}
                    <div className="hidden md:block min-w-0 text-right md:order-1">
                        <h3 className="text-lg sm:text-xl font-bold text-white">روابط سريعة</h3>
                        <FooterLinkList links={fastLinks} />
                    </div>

                    {/* Services — desktop only, accordion covers mobile */}
                    <div className="hidden md:block min-w-0 text-right md:order-2">
                        <h3 className="text-lg sm:text-xl font-bold text-white">خدماتنا</h3>
                        <FooterLinkList links={servicesLinks} />
                    </div>

                    {/* Industries — real sector landing pages, extra
                        crawlable internal links for SEO. Desktop only,
                        accordion covers mobile. */}
                    <div className="hidden md:block min-w-0 text-right md:order-3">
                        <h3 className="text-lg sm:text-xl font-bold text-white">القطاعات</h3>
                        <FooterLinkList links={industriesLinks} />
                    </div>

                    {/* Newsletter + trust badges — always visible. Extra
                        top margin on mobile only, so it doesn't sit flush
                        against the link accordion right above it; cancelled
                        on desktop where it's a grid column, not a stack. */}
                    <div className="wz-footer-news min-w-0 text-center mt-10 md:mt-0 md:order-4 max-w-[420px] mx-auto">
                        <h3 className="text-lg sm:text-xl font-bold text-white tracking-normal">النشرة البريدية</h3>
                        <p className="mt-2.5 text-[rgba(255,255,255,0.82)] font-medium leading-[1.9] tracking-normal">
                            اشترك واحصل على نصائح حصرية في التسويق والتصميم الرقمي
                            مباشرة لبريدك.
                        </p>
                        <form
                            onSubmit={handleNewsletterSubmit}
                            className="mt-6 relative flex items-center"
                        >
                            <input
                                type="email"
                                required
                                value={newsletterEmail}
                                onChange={(e) => setNewsletterEmail(e.target.value)}
                                placeholder="بريدك الإلكتروني"
                                className="w-full min-w-0 rounded-full border border-white bg-white/[0.06] py-3 pr-4 pl-14 sm:py-3.5 text-white text-right placeholder:text-white/90 focus:outline-none disabled:opacity-60"
                                disabled={subscribing}
                            />
                            <button
                                type="submit"
                                aria-label="اشترك"
                                disabled={subscribing}
                                className="absolute left-1.5 flex items-center justify-center size-9 sm:size-10 rounded-full bg-[#1f5bb7] text-white hover:bg-[#1a4d9c] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <ChevronLeft className="size-5" />
                            </button>
                        </form>

                        <div className="mt-8 space-y-3">
                            <a className="block text-center" href="/">
                                <img
                                    src={logo}
                                    alt="Wiz Freelance"
                                    loading="lazy"
                                    className="h-8! w-auto block mx-auto brightness-0 invert opacity-90"
                                />
                            </a>
                            <p className="text-white/90 text-sm font-semibold text-center">
                                موثوقون من قبل:
                            </p>
                            <style>{`@media (min-width:1024px){.wz-trust-badges>*{padding-left:8px!important;padding-right:8px!important}.wz-trust-badges span{font-size:9px!important;letter-spacing:0!important}.wz-trust-badges span+span{font-size:11px!important}}`}</style>
                            <div className="wz-trust-badges grid grid-cols-2 gap-3 mx-auto">
                                <div className="flex h-[54px] items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white px-3 py-2 shadow-sm">
                                    <img
                                        src={metaIcon}
                                        alt="Meta Verified"
                                        loading="lazy"
                                        className="h-5! w-5! shrink-0"
                                    />
                                    <div className="flex flex-col leading-snug">
                                        <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                            Meta Verified
                                        </span>
                                        <span className="text-xs font-semibold text-slate-700">
                                            Partner
                                        </span>
                                    </div>
                                </div>
                                <div className="flex h-[54px] items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white px-3 py-2 shadow-sm">
                                    <img
                                        src={googleIcon}
                                        alt="Google Verified"
                                        loading="lazy"
                                        className="h-5! w-5! shrink-0"
                                    />
                                    <div className="flex flex-col leading-snug">
                                        <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                            Google Verified
                                        </span>
                                        <span className="text-xs font-semibold text-slate-700">
                                            Partner
                                        </span>
                                    </div>
                                </div>
                                <a
                                    href="https://qr.saudibusiness.gov.sa/viewcr?nCrNumber=iuufilqQIEYsHreke0skJQ=="
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-[54px] items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white px-3 py-2 shadow-sm"
                                >
                                    <img
                                        src={ministryLogo}
                                        alt="Ministry of Commerce"
                                        loading="lazy"
                                        className="h-6! w-auto shrink-0"
                                    />
                                    <img
                                        src={vatLogo}
                                        alt="VAT Certificate"
                                        loading="lazy"
                                        className="h-6! w-auto shrink-0"
                                    />
                                </a>
                                <a
                                    href="https://find-and-update.company-information.service.gov.uk/company/16053684"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-[54px] items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white px-3 py-2 shadow-sm"
                                >
                                    <div className="flex flex-col leading-snug">
                                        <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                            WIZFREELANCE LTD · UK
                                        </span>
                                        <span className="text-xs font-semibold text-slate-700">
                                            Company No. 16053684
                                        </span>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom bar — desktop merges both rows into one (copyright ·
                standards · social), see FOOTER_DESKTOP_CSS. */}
            <div className="wrapper wz-footer-wide wz-footer-bottom">
                <div className="border-t border-white/10 pt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
                    <span className="wz-footer-std-label text-white/90 text-[11px]">
                        نعمل وفق معايير الويب العالمية:
                    </span>
                    {[
                        { label: "Google Search Central", href: "https://developers.google.com/search" },
                        { label: "PageSpeed Insights", href: "https://pagespeed.web.dev/" },
                        { label: "Schema.org", href: "https://schema.org/" },
                        { label: "W3C", href: "https://www.w3.org/" },
                    ].map(({ label, href }) => (
                        <a
                            key={label}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/90 text-[11px] hover:text-white underline underline-offset-2 transition-colors"
                            // ≥24px tap target (Lighthouse target-size) without changing the look.
                            style={{ display: "inline-flex", alignItems: "center", minHeight: 32, padding: "0 4px" }}
                        >
                            {label}
                        </a>
                    ))}
                </div>
                <div className="border-t border-white/10 py-6 flex flex-col-reverse md:flex-row items-center gap-5 sm:gap-6 justify-between text-center">
                    <p className="text-white/90 text-[13px] text-center">
                        © 2026 ويز فريلانس. جميع الحقوق محفوظة.
                    </p>

                    <div className="wz-footer-social flex items-center justify-center gap-2 sm:gap-2.5">
                        {SITE_SOCIAL_LINKS.map(({ label, href, Icon }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                className="flex items-center justify-center size-11 rounded-full border border-white/15 text-white/90 hover:bg-white/10 hover:border-white/30 hover:scale-110 transition-all"
                            >
                                <Icon className="size-5" />
                            </a>
                        ))}
                    </div>

                    <div className="lg:hidden flex h-[54px] items-center gap-2 rounded-xl border border-white/10 bg-white px-3 py-2 shadow-sm mt-2 mb-2 md:mt-0 md:mb-0 mx-auto md:mx-0">
                        <ShieldCheck className="size-5 text-emerald-500 shrink-0" />
                        <div className="flex flex-col leading-snug text-right">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                SSL · 256-bit
                            </span>
                            <span className="text-xs font-semibold text-slate-700">
                                اتصال آمن ومشفر
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
